import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import SelectClearAllControl from "@/components/select-clear-all-control";
import SelectClearAllRowControl from "@/components/select-clear-all-row-control";
import { useTableSettings } from "@/hooks/use-table-settings";
import { StatVisualizationGroupDto } from "@/shared/api/models/stat-visualization-group-dto";

import { createCheckboxColumn } from "./util";

const StatvisualizationForm = ({
  name = "",
  groups,
  onSelectColumn,
  onClearColumn,
  onSelectRow,
  onClearRow,
  onSelectAll,
  onClearAll,
}: {
  name?: string;
  groups: StatVisualizationGroupDto[];
  onSelectColumn?: (column: string) => void;
  onClearColumn?: (column: string) => void;
  onSelectRow?: (groupName: string) => void;
  onClearRow?: (groupName: string) => void;
  onSelectAll?: () => void;
  onClearAll?: () => void;
}) => {
  const { t } = useTranslation();

  const columns = useMemo<MRT_ColumnDef<StatVisualizationGroupDto>[]>(
    () => [
      {
        header: t("groupName"),
        accessorFn: (row) => row.name,
      },
      createCheckboxColumn<StatVisualizationGroupDto, "eco">({
        columnName: "eco",
        headerText: "eco",
        formInputName: (row) => `${name}.${row.index}.eco`,
        onSelectColumn: (column) => onSelectColumn?.(column),
        onClearColumn: (column) => onClearColumn?.(column),
      }),
      createCheckboxColumn<StatVisualizationGroupDto, "adv">({
        columnName: "adv",
        headerText: "adv",
        formInputName: (row) => `${name}.${row.index}.adv`,
        onSelectColumn: (column) => onSelectColumn?.(column),
        onClearColumn: (column) => onClearColumn?.(column),
      }),
      createCheckboxColumn<StatVisualizationGroupDto, "aicon">({
        columnName: "aicon",
        headerText: "aicon",
        formInputName: (row) => `${name}.${row.index}.aicon`,
        onSelectColumn: (column) => onSelectColumn?.(column),
        onClearColumn: (column) => onClearColumn?.(column),
      }),
    ],
    [t, name, onSelectColumn, onClearColumn]
  );

  const { defaultProps, state } = useTableSettings<StatVisualizationGroupDto>("userSvGroup");

  const table = useMaterialReactTable({
    data: groups,
    columns,
    enableColumnActions: true,
    enableRowActions: true,
    getRowId: (originalRow) => originalRow.name,
    enableStickyHeader: true,
    enablePagination: false,
    enableGrouping: true,
    enableColumnFilterModes: true,
    enableColumnDragging: false,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
      sorting: [{ id: "name", desc: false }],
    },
    state: {
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    renderTopToolbarCustomActions: () => (
      <div>
        <SelectClearAllControl onSelectAll={onSelectAll} onClearAll={onClearAll} />
      </div>
    ),
    renderRowActions: ({ row: { original } }) => (
      <SelectClearAllRowControl
        onSelectRow={onSelectRow}
        onClearRow={onClearRow}
        name={original.name}
      />
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default StatvisualizationForm;
